import { z } from "@hono/zod-openapi";
//#region src/errors.ts
var HttpError = class extends Error {
	statusCode;
	constructor(statusCode, message) {
		super(message);
		this.statusCode = statusCode;
		this.name = "HttpError";
	}
};
var BadRequestError = class extends HttpError {
	constructor(message = "Bad Request") {
		super(400, message);
		this.name = "BadRequestError";
	}
};
var UnauthorizedError = class extends HttpError {
	constructor(message = "Unauthorized") {
		super(401, message);
		this.name = "UnauthorizedError";
	}
};
var ForbiddenError = class extends HttpError {
	constructor(message = "Forbidden") {
		super(403, message);
		this.name = "ForbiddenError";
	}
};
var NotFoundError = class extends HttpError {
	constructor(message = "Not Found") {
		super(404, message);
		this.name = "NotFoundError";
	}
};
var ConflictError = class extends HttpError {
	constructor(message = "Conflict") {
		super(409, message);
		this.name = "ConflictError";
	}
};
var TooManyRequestsError = class extends HttpError {
	constructor(message = "Too Many Requests") {
		super(429, message);
		this.name = "TooManyRequestsError";
	}
};
var InternalServerError = class extends HttpError {
	constructor(message = "Internal Server Error") {
		super(500, message);
		this.name = "InternalServerError";
	}
};
const isRecord = (value) => {
	return typeof value === "object" && value !== null;
};
const extractErrorMessage = (data) => {
	if (typeof data === "string") return data;
	if (isRecord(data) && typeof data["message"] === "string") return data["message"];
	return "Request failed";
};
const toHttpError = (res) => {
	const error = res.json().catch(() => "JSON error");
	const message = extractErrorMessage(error);
	switch (res.status) {
		case 400: return new BadRequestError(message);
		case 401: return new UnauthorizedError(message);
		case 403: return new ForbiddenError(message);
		case 404: return new NotFoundError(message);
		case 409: return new ConflictError(message);
		case 429: return new TooManyRequestsError(message);
		case 500: return new InternalServerError(message);
		default: return new HttpError(res.status, message);
	}
};
//#endregion
//#region src/client.ts
const createClient = (opts = {}) => {
	const baseUrl = opts.baseUrl ?? "http://localhost:8080";
	const buildHeaders = (params) => {
		const headers = new Headers(typeof opts.headers === "function" ? opts.headers(params) : opts.headers);
		headers.append("Content-Type", "application/json");
		return headers;
	};
	const request = async (request) => {
		const { method, path, body, query, params } = request;
		const url = new URL(baseUrl);
		url.pathname = `${url.pathname.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
		if (params) for (const [key, value] of Object.entries(params)) url.pathname = url.pathname.replace(`%7B${key}%7D`, value);
		if (query) {
			for (const [key, value] of Object.entries(query)) if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, v));
			else if (value !== void 0) url.searchParams.append(key, String(value));
		}
		const init = {
			method,
			headers: buildHeaders(request),
			...opts.credentials ? { credentials: opts.credentials } : {}
		};
		if (body) init.body = JSON.stringify(body);
		const response = await fetch(url, init);
		if (!response.ok) throw toHttpError(response);
		const text = await response.text();
		if (!text) return;
		return JSON.parse(text);
	};
	return {
		get: ({ path, query, params }) => request({
			method: "GET",
			path,
			query,
			params
		}),
		post: ({ path, body, params }) => request({
			method: "POST",
			path,
			body,
			params
		}),
		put: ({ path, body, params }) => request({
			method: "PUT",
			path,
			body,
			params
		}),
		patch: ({ path, body, params }) => request({
			method: "PATCH",
			path,
			body,
			params
		}),
		delete: ({ path, params }) => request({
			method: "DELETE",
			path,
			params
		})
	};
};
const include = (includes) => z.union([z.enum(includes), z.array(z.enum(includes))]).optional();
//#endregion
//#region src/subscriptions.ts
const SubscriptionStatus = z.enum([
	"trialing",
	"active",
	"canceled",
	"incomplete",
	"incomplete_expired",
	"past_due",
	"unpaid",
	"paused"
]);
const Subscription = z.object({
	id: z.uuid(),
	stripeSubscriptionId: z.string(),
	status: SubscriptionStatus,
	quantity: z.number().int(),
	cancelAtPeriodEnd: z.boolean(),
	currentPeriodStart: z.coerce.date().optional(),
	currentPeriodEnd: z.coerce.date().optional(),
	endedAt: z.coerce.date().optional(),
	cancelAt: z.coerce.date().optional(),
	canceledAt: z.coerce.date().optional(),
	trialStart: z.coerce.date().optional(),
	trialEnd: z.coerce.date().optional(),
	userId: z.uuid(),
	stripeCustomerId: z.string(),
	stripePriceId: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Subscription");
const GetCurrentSubscriptionResponse = z.object({ subscription: Subscription }).openapi("GetCurrentSubscriptionResponse");
const subscriptions = (client) => ({ getCurrent: () => client.get({ path: "/subscriptions/current" }) });
//#endregion
//#region src/users.ts
const UserRole = z.enum(["TESTER", "ADMIN"]).openapi("UserRole");
const SubscriptionType = z.enum([
	"FREE",
	"PAID",
	"INNER_CIRCLE",
	"OUTER_CIRCLE"
]);
const isPaidSubscription = (user) => [
	SubscriptionType.enum.PAID,
	SubscriptionType.enum.INNER_CIRCLE,
	SubscriptionType.enum.OUTER_CIRCLE
].some((type) => type === user?.subscriptionType) || [UserRole.enum.ADMIN, UserRole.enum.TESTER].some((role) => role === user?.role);
const canAccessInnerCircle = (user) => user?.subscriptionType === SubscriptionType.enum.INNER_CIRCLE || [UserRole.enum.ADMIN, UserRole.enum.TESTER].some((role) => role === user?.role);
const canAccessOuterCircle = (user) => canAccessInnerCircle(user) || user?.subscriptionType === SubscriptionType.enum.OUTER_CIRCLE;
const AcquisitionType = z.enum([
	"ORGANIC",
	"BOOK",
	"INSTAGRAM",
	"FACEBOOK"
]);
const User = z.object({
	id: z.uuid(),
	email: z.email(),
	role: UserRole.optional(),
	subscriptionType: SubscriptionType,
	acquisitionType: AcquisitionType,
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("User");
const GetUsersQuery = z.object({
	include: include(["subscriptions"]),
	email: z.string().optional().openapi({ description: "Case-insensitive search against email" }),
	pageSize: z.coerce.number().int().min(1).max(100).default(10).optional(),
	pageToken: z.string().optional()
}).openapi("GetUsersQuery");
const GetUsersResponse = z.object({
	users: User.array(),
	subscriptions: Subscription.array().optional(),
	nextPageToken: z.string().optional()
}).openapi("GetUsersResponse");
const GetUserPath = z.object({ userId: z.string() }).openapi("GetUserPath");
const GetUserResponse = z.object({ user: User }).openapi("GetUserResponse");
const UpdateUserRequest = z.object({
	role: UserRole.nullable().optional(),
	subscriptionType: SubscriptionType.optional()
}).openapi("UpdateUserRequest");
const UpdateUserResponse = z.object({ user: User }).openapi("UpdateUserResponse");
const GetUnlockedCardsResponse = z.object({ cards: z.array(z.string()) }).openapi("GetUnlockedCardsResponse");
const GetMeResponse = z.object({
	user: User,
	nextReadingDate: z.iso.datetime().optional(),
	level: z.number().optional()
}).openapi("GetMeResponse");
const users = (client) => ({
	list: (query = {}) => client.get({
		path: "/users",
		query
	}),
	getMe: () => client.get({ path: "/users/me" }),
	get: (params) => client.get({
		path: "/users/{userId}",
		params
	}),
	update: (params, body) => client.patch({
		path: "/users/{userId}",
		params,
		body
	}),
	getUnlockedCards: () => client.get({ path: "/users/cards/unlocked" })
});
//#endregion
//#region src/auth.ts
const AuthUser = z.object({
	id: z.string(),
	roles: UserRole.array()
}).openapi("AuthUser");
const OtpRequest = z.object({
	email: z.email(),
	redirectTo: z.string().optional()
}).openapi("OtpRequest");
const OtpResponse = z.object({ message: z.string() }).openapi("OtpResponse");
const OtpVerifyRequest = z.object({
	email: z.email(),
	code: z.string().regex(/^\d{6}$/)
}).openapi("OtpVerifyRequest");
const OtpVerifyResponse = z.object({ accessToken: z.string() }).openapi("OtpVerifyResponse");
const TokenRefreshResponse = z.object({ accessToken: z.string() }).openapi("TokenRefreshResponse");
const LogoutResponse = z.object({ message: z.string() }).openapi("LogoutResponse");
const auth = (client) => ({
	requestOtp: (body) => client.post({
		path: "/auth/otp",
		body
	}),
	verifyOtp: (body) => client.post({
		path: "/auth/otp/verify",
		body
	}),
	refreshToken: () => client.post({ path: "/auth/refresh" }),
	logout: () => client.post({ path: "/auth/logout" })
});
//#endregion
//#region src/decks.ts
const Deck = z.object({
	id: z.uuid(),
	name: z.string(),
	slug: z.string(),
	description: z.string().optional(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Deck");
const GetDecksResponse = z.object({ decks: z.array(Deck) }).openapi("GetDecksResponse");
const decks = (client) => ({ list: () => client.get({ path: "/decks" }) });
//#endregion
//#region src/cards.ts
const Card = z.object({
	id: z.uuid(),
	name: z.string(),
	slug: z.string(),
	description: z.string().optional(),
	deckId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Card");
const GetCardsQuery = z.object({
	deckId: z.uuid().optional(),
	include: include(["decks"])
}).openapi("GetCardsQuery");
const GetCardsResponse = z.object({
	cards: z.array(Card),
	decks: Deck.array().optional()
}).openapi("GetCardsResponse");
const cards = (client) => ({ list: (query = {}) => client.get({
	path: "/cards",
	query
}) });
//#endregion
//#region src/customers.ts
const Customer = z.object({
	id: z.uuid(),
	userId: z.string(),
	stripeCustomerId: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Customer");
const CreateCheckoutRequest = z.object({
	priceId: z.uuid(),
	returnTo: z.string().optional()
}).openapi("CreateCheckoutRequest");
const CreateCheckoutResponse = z.object({ url: z.string() }).openapi("CreateCheckoutResponse");
const CreatePortalResponse = z.object({ url: z.string() }).openapi("CreatePortalResponse");
const customers = (client) => ({
	createCheckout: (body) => client.post({
		path: "/customers/checkout",
		body
	}),
	createPortal: () => client.post({ path: "/customers/portal" })
});
//#endregion
//#region src/draws.ts
const Draw = z.object({
	id: z.uuid(),
	cardId: z.uuid(),
	positionId: z.uuid(),
	readingId: z.uuid(),
	userId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Draw");
const DrawPattern = z.object({
	year: z.number().int(),
	month: z.number().int().min(1).max(12),
	cards: z.array(z.object({
		cardId: z.uuid(),
		count: z.number().int()
	}))
}).openapi("DrawPattern");
const GetDrawsPatternsResponse = z.object({ patterns: z.array(DrawPattern) }).openapi("GetDrawsPatternsResponse");
const draws = (client) => ({ patterns: () => client.get({ path: "/draws/patterns" }) });
//#endregion
//#region src/feeds.ts
const FeedCard = z.object({
	id: z.uuid(),
	name: z.string(),
	slug: z.string(),
	description: z.string().optional(),
	deckSlug: z.string()
});
const FeedReading = z.object({
	id: z.uuid(),
	createdAt: z.coerce.date(),
	cards: z.array(FeedCard)
});
const FeedPage = z.object({
	title: z.string(),
	slug: z.string(),
	excerpt: z.string()
});
const FeedItem = z.discriminatedUnion("type", [
	z.object({
		id: z.uuid(),
		type: z.literal("reading"),
		occurredAt: z.coerce.date(),
		reading: FeedReading
	}),
	z.object({
		id: z.uuid(),
		type: z.literal("note"),
		occurredAt: z.coerce.date(),
		note: z.object({
			id: z.uuid(),
			content: z.string(),
			createdAt: z.coerce.date(),
			updatedAt: z.coerce.date().optional()
		}),
		reading: FeedReading.optional()
	}),
	z.object({
		id: z.uuid(),
		type: z.literal("monthly_pattern"),
		occurredAt: z.coerce.date(),
		pattern: z.object({
			id: z.uuid(),
			year: z.number().int(),
			month: z.number().int(),
			cards: z.array(z.object({
				card: FeedCard,
				count: z.number().int()
			}))
		})
	}),
	z.object({
		id: z.uuid(),
		type: z.literal("card_unlock"),
		occurredAt: z.coerce.date(),
		card: FeedCard
	}),
	z.object({
		id: z.uuid(),
		type: z.literal("page"),
		occurredAt: z.coerce.date(),
		page: FeedPage
	})
]).openapi("FeedItem");
const GetFeedsQuery = z.object({
	pageSize: z.coerce.number().int().min(1).max(25).default(10).optional(),
	pageToken: z.string().optional()
}).openapi("GetFeedsQuery");
const GetFeedsResponse = z.object({
	items: z.array(FeedItem),
	nextPageToken: z.string().optional()
}).openapi("GetFeedsResponse");
const feeds = (client) => ({ list: (query = {}) => client.get({
	path: "/feeds",
	query
}) });
//#endregion
//#region src/notes.ts
const Note = z.object({
	id: z.uuid(),
	content: z.string(),
	readingId: z.uuid().optional(),
	userId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Note");
const NoteWithReading = Note.extend({
	readingId: z.uuid(),
	readingCreatedAt: z.coerce.date(),
	draws: z.array(Draw).optional()
}).openapi("NoteWithReading");
const GetNotePath = z.object({ noteId: z.string() }).openapi("GetNotePath");
const GetNoteResponse = z.object({ note: Note }).openapi("GetNoteResponse");
const GetNotesQuery = z.object({
	pageSize: z.coerce.number().int().min(1).max(50).default(20).optional(),
	pageToken: z.string().optional()
}).openapi("GetNotesQuery");
const GetNotesResponse = z.object({
	notes: z.array(NoteWithReading),
	nextPageToken: z.string().optional()
}).openapi("GetNotesResponse");
const UpsertNoteRequest = z.object({
	readingId: z.string(),
	content: z.string()
}).openapi("UpsertNoteRequest");
const UpsertNoteResponse = z.object({ note: Note }).openapi("UpsertNoteResponse");
const CreateNoteResponse = z.object({ note: Note }).openapi("CreateNoteResponse");
const CreateNoteRequest = z.object({ content: z.string().min(1) }).openapi("CreateNoteRequest");
const notes = (client) => ({
	list: (query = {}) => client.get({
		path: "/notes",
		query
	}),
	get: (params) => client.get({
		path: "/notes/{noteId}",
		params
	}),
	upsert: (body) => client.put({
		path: "/notes",
		body
	}),
	create: (body) => client.post({
		path: "/notes",
		body
	})
});
//#endregion
//#region src/positions.ts
const Position = z.object({
	id: z.uuid(),
	name: z.string(),
	description: z.string().optional(),
	order: z.number().int(),
	x: z.number(),
	y: z.number(),
	rotate: z.number().optional(),
	spreadId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Position");
//#endregion
//#region src/prices.ts
const PriceType = z.enum(["one_time", "recurring"]);
const PriceRecurringInterval = z.enum([
	"day",
	"week",
	"month",
	"year"
]);
const Price = z.object({
	id: z.uuid(),
	stripePriceId: z.string(),
	lookupKey: z.string().optional(),
	isActive: z.boolean(),
	name: z.string().optional(),
	amount: z.number().optional(),
	currency: z.string(),
	type: PriceType,
	recurringInterval: PriceRecurringInterval.optional(),
	recurringIntervalCount: z.number().int().optional(),
	recurringTrialPeriodDays: z.number().int().optional(),
	stripeProductId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Price");
//#endregion
//#region src/products.ts
const Product = z.object({
	id: z.uuid(),
	stripeProductId: z.string(),
	isActive: z.boolean(),
	name: z.string(),
	description: z.string().optional(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Product");
const GetProductsQuery = z.object({ include: include(["prices"]) }).openapi("GetProductsQuery");
const GetProductsResponse = z.object({
	products: Product.array(),
	prices: Price.array().optional()
}).openapi("GetProductsResponse");
const products = (client) => ({ get: (query = {}) => client.get({
	path: "/products",
	query
}) });
//#endregion
//#region src/push.ts
const PushSubscription = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	endpoint: z.url(),
	p256dh: z.string(),
	auth: z.string(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("PushSubscription");
const GetVapidPublicKeyResponse = z.object({ vapidPublicKey: z.string() }).openapi("GetVapidPublicKeyResponse");
const CreatePushSubscriptionBody = z.object({
	endpoint: z.url(),
	keys: z.object({
		p256dh: z.string(),
		auth: z.string()
	})
}).openapi("CreatePushSubscriptionBody");
const CreatePushSubscriptionResponse = z.object({ subscription: PushSubscription }).openapi("CreatePushSubscriptionResponse");
const GetPushSubscriptionsResponse = z.object({ subscriptions: z.array(PushSubscription) }).openapi("GetPushSubscriptionsResponse");
const DeletePushSubscriptionPath = z.object({ subscriptionId: z.uuid() }).openapi("DeletePushSubscriptionPath");
const SendNotificationPath = z.object({ userId: z.uuid() }).openapi("SendNotificationPath");
const SendNotificationBody = z.object({ payload: z.object({
	title: z.string(),
	body: z.string()
}) }).openapi("SendNotificationBody");
const push = (client) => ({
	getVapidPublicKey: () => client.get({ path: "/push/vapid-public-key" }),
	subscribe: (body) => client.post({
		path: "/push/subscriptions",
		body
	}),
	list: () => client.get({ path: "/push/subscriptions" }),
	unsubscribe: (params) => client.delete({
		path: "/push/subscriptions/{subscriptionId}",
		params
	}),
	sendNotification: ({ userId, payload }) => client.post({
		path: "/push/users/{userId}/notifications",
		params: { userId },
		body: { payload }
	})
});
//#endregion
//#region src/readings.ts
const Reading = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	spreadId: z.uuid(),
	deckId: z.uuid(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Reading");
const GetReadingsQuery = z.object({
	userId: z.uuid().optional(),
	include: include(["draws", "notes"]),
	pageSize: z.coerce.number().int().min(1).max(25).default(10).optional(),
	pageToken: z.string().optional()
}).openapi("GetReadingsQuery");
const GetReadingsResponse = z.object({
	readings: z.array(Reading),
	draws: z.array(Draw).optional(),
	notes: z.array(Note).optional(),
	nextPageToken: z.string().optional()
}).openapi("GetReadingsResponse");
const GetReadingPath = z.object({ readingId: z.uuid() }).openapi("GetReadingPath");
const GetReadingQuery = z.object({ include: include(["draws"]) }).openapi("GetReadingQuery");
const GetReadingResponse = z.object({
	reading: Reading,
	draws: Draw.array().optional()
}).openapi("GetReadingResponse");
const GetReadingNotePath = z.object({ readingId: z.uuid() }).openapi("GetReadingNotePath");
const GetReadingNoteResponse = z.object({ note: Note }).openapi("GetReadingNoteResponse");
const CreateReadingBody = z.object({
	spreadId: z.uuid(),
	deckId: z.uuid()
}).openapi("CreateReadingBody");
const CreateReadingResponse = z.object({
	reading: Reading,
	draws: Draw.array()
}).openapi("CreateReadingResponse");
const readings = (client) => ({
	list: (query = {}) => client.get({
		path: "/readings",
		query
	}),
	get: ({ readingId, ...query }) => client.get({
		path: "/readings/{readingId}",
		params: { readingId },
		query
	}),
	getNote: (params) => client.get({
		path: "/readings/{readingId}/note",
		params
	}),
	create: (body) => client.post({
		path: "/readings",
		body
	})
});
//#endregion
//#region src/spreads.ts
const Spread = z.object({
	id: z.uuid(),
	name: z.string(),
	slug: z.string(),
	description: z.string().optional(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date().optional()
}).openapi("Spread");
const GetSpreadsQuery = z.object({ include: include(["positions"]) }).openapi("GetSpreadsQuery");
const GetSpreadsResponse = z.object({
	spreads: z.array(Spread),
	positions: z.array(Position).optional()
}).openapi("GetSpreadsResponse");
const spreads = (client) => ({ list: (query = {}) => client.get({
	path: "/spreads",
	query
}) });
//#endregion
//#region src/index.ts
const createApi = (opts = {}) => {
	const client = createClient(opts);
	return {
		auth: auth(client),
		cards: cards(client),
		customers: customers(client),
		decks: decks(client),
		draws: draws(client),
		feeds: feeds(client),
		notes: notes(client),
		products: products(client),
		push: push(client),
		readings: readings(client),
		spreads: spreads(client),
		subscriptions: subscriptions(client),
		users: users(client)
	};
};
//#endregion
export { AcquisitionType, AuthUser, BadRequestError, Card, ConflictError, CreateCheckoutRequest, CreateCheckoutResponse, CreateNoteRequest, CreateNoteResponse, CreatePortalResponse, CreatePushSubscriptionBody, CreatePushSubscriptionResponse, CreateReadingBody, CreateReadingResponse, Customer, Deck, DeletePushSubscriptionPath, Draw, DrawPattern, FeedCard, FeedItem, FeedPage, FeedReading, ForbiddenError, GetCardsQuery, GetCardsResponse, GetCurrentSubscriptionResponse, GetDecksResponse, GetDrawsPatternsResponse, GetFeedsQuery, GetFeedsResponse, GetMeResponse, GetNotePath, GetNoteResponse, GetNotesQuery, GetNotesResponse, GetProductsQuery, GetProductsResponse, GetPushSubscriptionsResponse, GetReadingNotePath, GetReadingNoteResponse, GetReadingPath, GetReadingQuery, GetReadingResponse, GetReadingsQuery, GetReadingsResponse, GetSpreadsQuery, GetSpreadsResponse, GetUnlockedCardsResponse, GetUserPath, GetUserResponse, GetUsersQuery, GetUsersResponse, GetVapidPublicKeyResponse, HttpError, InternalServerError, LogoutResponse, NotFoundError, Note, NoteWithReading, OtpRequest, OtpResponse, OtpVerifyRequest, OtpVerifyResponse, Position, Price, PriceRecurringInterval, PriceType, Product, PushSubscription, Reading, SendNotificationBody, SendNotificationPath, Spread, Subscription, SubscriptionStatus, SubscriptionType, TokenRefreshResponse, TooManyRequestsError, UnauthorizedError, UpdateUserRequest, UpdateUserResponse, UpsertNoteRequest, UpsertNoteResponse, User, UserRole, auth, canAccessInnerCircle, canAccessOuterCircle, cards, createApi, createClient, customers, decks, draws, feeds, isPaidSubscription, notes, products, push, readings, spreads, subscriptions, toHttpError, users };

//# sourceMappingURL=index.mjs.map